import { createContext, useContext, ReactNode } from 'react';
import { useProfile, CURRENCY_OPTIONS } from '@/hooks/useProfile';

interface FormatCurrencyOptions {
  minimumFractionDigits?: number; 
  maximumFractionDigits?: number;
  notation?: 'standard' | 'compact';
}

interface CurrencyContextType {
  currency: string;
  symbol: string;
  formatCurrency: (value: number, options?: FormatCurrencyOptions) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const { profile } = useProfile();
  
  const currency = profile?.currency || 'USD';
  const currencyOption = CURRENCY_OPTIONS.find(c => c.value === currency);
  const symbol = currencyOption?.symbol || '$';

  const formatCurrency = (value: number, options?: FormatCurrencyOptions) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: options?.minimumFractionDigits ?? 0,
      maximumFractionDigits: options?.maximumFractionDigits ?? 0,
      notation: options?.notation ?? 'standard',
    }).format(value);
  };

  return (
    <CurrencyContext.Provider value={{ currency, symbol, formatCurrency }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    // Fallback for when used outside provider
    return {
      currency: 'USD',
      symbol: '$',
      formatCurrency: (value: number, options?: FormatCurrencyOptions) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: options?.minimumFractionDigits ?? 0,
          maximumFractionDigits: options?.maximumFractionDigits ?? 0,
          notation: options?.notation ?? 'standard',
        }).format(value);
      },
    };
  }
  return context;
}
